import {
  Box,
  Button,
  Typography,
  styled,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Radio,
} from "@mui/material";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import uuid from "react-uuid";
import {
  DataGrid,
  GridRowModes,
  GridRowModesModel,
  GridActionsCellItem,
  GridRowId,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  GridEditInputCell,
} from "@mui/x-data-grid";
import { AttendanceStatus, validateEmail } from "./utils";
import { RowModel } from "AttendanceForm/Types/types";
import { FormikProps } from "formik";
import { useStyles } from "./LearnerDetailsStyles";

interface LearnerDetailsTableProps {
  errorMessage: string;
  rows: RowModel[];
  setRows: Dispatch<SetStateAction<RowModel[]>>;
  form: FormikProps<any>;
  setErrorMessage: Dispatch<SetStateAction<string>>;
}

const LearnerDetailsTable: React.FC<LearnerDetailsTableProps> = ({
  errorMessage,
  rows,
  setRows,
  form,
  setErrorMessage,
}) => {
  const { t } = useTranslation("learnerDetailsForm");
  const styles = useStyles();
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  // Clear error message when there are no rows (instead of doing it in noRowsOverlay render)
  useEffect(() => {
    if (rows.length === 0) {
      setErrorMessage("");
    }
  }, [rows.length, setErrorMessage]);

  const handleEditingRow = (isEditing: boolean, id: string | number) => {
    const updatedRow = rows.find((r) => r.id === id);
    updatedRow.isEditing = isEditing;
    const newRows = rows.map((row) => (row.id === id ? updatedRow : row));
    setRows(newRows as RowModel[]);
    form.setFieldValue("allAttendees", newRows);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (params) => {
    handleEditingRow(true, params.id);
    setErrorMessage("");
  };

  const handleEditClick = (id: GridRowId) => () => {
    setErrorMessage("");
    handleEditingRow(true, id);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    handleEditingRow(false, id);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    const deletedEmail = rows.find((row) => row.id === id).email;
    const newRows = rows.filter((row) => row.id !== id);
    setRows(newRows);
    if (
      newRows.filter((row) => row.email === deletedEmail).length <= 1 &&
      deletedEmail.trim() != ""
    )
      setErrorMessage("");

    form.setFieldValue("allAttendees", newRows);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    editedRow.isEditing = false;
    if (editedRow!.isNew) {
      const newRows = rows.filter((row) => row.id !== id);
      setRows(newRows);
      form.setFieldValue("allAttendees", newRows);
    } else {
      setRows(rows);
      form.setFieldValue("allAttendees", rows);
    }
  };

  const processRowUpdate = (newRow: RowModel) => {
    let updatedRow = {};
    if (!isEmailDuplicate(newRow.email, newRow.isNew, newRow.id)) {
      updatedRow = { ...newRow, isNew: false, hasError: false, isEditing: false };
    } else {
      updatedRow = { ...newRow, isNew: false, hasError: true, isEditing: false };
    }
    const newRows = rows.map((row) => (row.id === newRow.id ? updatedRow : row));
    setRows(newRows as RowModel[]);
    form.setFieldValue("allAttendees", newRows);
    return updatedRow;
  };

  const isEmailDuplicate = (email: string, isNewRow: boolean, id: string): boolean => {
    let emailTaken = false;
    if (isNewRow) {
      const existingLearnerEmails = rows
        .filter((x) => x.email && x.email.trim())
        .map((row) => row.email.toLowerCase());
      emailTaken = existingLearnerEmails.includes(email.toLowerCase());
    } else {
      const existingRowsWithSameEmail = rows.filter(
        (x) => x.id != id && x.email && x.email.trim().toLowerCase() == email.trim().toLowerCase(),
      );
      emailTaken = existingRowsWithSameEmail.length > 0;
    }

    const error = emailTaken ? email + t("emailTaken") : "";
    setErrorMessage(error);
    return emailTaken;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleAddLearnerClick = () => {
    const id = uuid();
    const emptyRow = {
      id,
      firstname: "",
      lastname: "",
      email: "",
      notes: "",
      attended: AttendanceStatus.Blank,
      isNew: true,
      hasError: false,
      isEditing: true,
    };
    setRows((oldRows) => [...oldRows, emptyRow]);
    form.setFieldValue("allAttendees", { ...rows, emptyRow });
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "firstname" },
    }));
  };

  let promiseTimeout;

  const validateEmailField = (email: string) => {
    return new Promise((resolve) => {
      promiseTimeout = setTimeout(
        () => {
          const validationError = validateEmail(email);
          let tooltipMessage = email;
          if (!email || !email.trim()) {
            tooltipMessage = t("emailBlank");
          } else {
            if (validationError) {
              tooltipMessage += t("invalidEmail");
            }
          }

          resolve(validationError ? tooltipMessage : null);
        },
        Math.random() * 500 + 100,
      ); // simulate network latency
    });
  };

  React.useEffect(() => {
    return () => {
      clearTimeout(promiseTimeout);
    };
  }, [promiseTimeout]);

  const preProcessEditCellProps = async (params: GridPreProcessEditCellProps) => {
    if (params.hasChanged) {
      const errorMessage = await validateEmailField(params.props.value);
      return { ...params.props, error: errorMessage };
    }
    return { ...params.props };
  };

  const EmailEditInputCell = (props: GridRenderEditCellParams) => {
    const { error } = props;
    return (
      <StyledTooltip open={!!error} title={error}>
        <GridEditInputCell {...props} />
      </StyledTooltip>
    );
  };

  const renderEditEmail = (params: GridRenderEditCellParams) => {
    return <EmailEditInputCell {...params} />;
  };

  const updateAttendenceStatus = (cellValues: any, attendenceStatus: AttendanceStatus) => {
    setErrorMessage("");
    const rowBeingUpdated = rows.find((r) => r.id === cellValues.id);
    const updatedRow = { ...rowBeingUpdated, attended: attendenceStatus, hasError: false };
    const newRows = rows.map((row) => (row.id === cellValues.id ? updatedRow : row));
    setRows(newRows as RowModel[]);
    form.setFieldValue("allAttendees", newRows);
    return cellValues.api.updateRows([{ id: cellValues.id, attended: attendenceStatus }]);
  };

  const noRowsOverlay = () => {
    return (
      <Box height="100%" display="flex" alignItems="center">
        {t("noRowsOne")} <b>{t("addLearner")}</b> {t("noRowsTwo")}
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "firstname",
      headerName: "First Name*",
      width: 180,
      editable: true,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "lastname",
      headerName: "Last Name*",
      width: 180,
      editable: true,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "email",
      headerName: "Work Email*",
      width: 220,
      editable: true,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      preProcessEditCellProps,
      renderEditCell: renderEditEmail,
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 220,
      editable: true,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "attended",
      renderCell: (cellValues) => {
        return (
          <FormControl>
            <RadioGroup row aria-labelledby="demo-form-control-label-placement" name="position">
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    id="rbAttended"
                    onChange={(e) => updateAttendenceStatus(cellValues, AttendanceStatus.Attended)}
                    value={AttendanceStatus.Attended}
                    checked={
                      rows.find((x) => x.id === cellValues.id).attended ===
                      AttendanceStatus.Attended
                    }
                  />
                }
                label="Attended"
                color="primary"
              />
              <FormControlLabel
                control={
                  <Radio
                    color="primary"
                    id="rbNotAttended"
                    onChange={(e) =>
                      updateAttendenceStatus(cellValues, AttendanceStatus.NotAttended)
                    }
                    value={AttendanceStatus.NotAttended}
                    checked={
                      rows.find((x) => x.id === cellValues.id).attended ===
                      AttendanceStatus.NotAttended
                    }
                  />
                }
                label="Not Attended"
                color="primary"
              />
            </RadioGroup>
          </FormControl>
        );
      },
      headerName: "Attended*",
      width: 300,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const isRowExistingAttendanceRecord = rows.find(
          (r) => r.id === id,
        ).isExistingAttendanceRecord;

        if (isInEditMode) {
          return [
            // eslint-disable-next-line react/jsx-key
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              color="primary"
              onClick={handleSaveClick(id)}
            />,
            // eslint-disable-next-line react/jsx-key
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          // eslint-disable-next-line react/jsx-key
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <>
            {/* eslint-disable-next-line react/jsx-key */}
            {!isRowExistingAttendanceRecord && (
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDeleteClick(id)}
                color="inherit"
              />
            )}
          </>,
        ];
      },
    },
  ];

  return (
    <Box>
      <Box className={styles.learnerGridBox}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          slots={{
            noRowsOverlay: noRowsOverlay,
          }}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          onRowEditStart={handleRowEditStart}
          processRowUpdate={processRowUpdate}
          isCellEditable={(params) => {
            return (
              !params.row.isExistingAttendanceRecord ||
              params.field === "attended" ||
              params.field === "notes"
            );
          }}
          hideFooter
          //@ts-ignore
          getRowClassName={(params) => (params.row.hasError ? `rowError` : "")}
        />
      </Box>
      <Box
        display="flex"
        alignContent="center"
        justifyContent="space-between"
        paddingTop={2}
        paddingBottom={2}
      >
        <Box display="flex" alignItems="center">
          <Button color="primary" variant="contained" onClick={handleAddLearnerClick}>
            {t("addLearner")}
          </Button>
          <Box paddingLeft={2}>
            <Typography variant="body1" color="error">
              {errorMessage}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1">
          {t("totalRows")} {rows.filter((x) => x.attended === AttendanceStatus.Attended).length}
        </Typography>
      </Box>
    </Box>
  );
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-overlayWrapper": {
    height: theme.spacing(10),
  },
  "& .MuiDataGrid-overlayWrapperInner": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold",
  },
  "& .MuiDataGrid-cell--editable": {
    "& .MuiInputBase-root": {
      height: "100%",
    },
  },
  "& .Mui-error": {
    color: "red",
  },
  "& .rowError": {
    backgroundColor: "rgb(244,67,54, 0.2)",
    "&:hover": {
      backgroundColor: "rgb(244,67,54, 0.2)",
    },
    "&.Mui-selected": {
      backgroundColor: "rgb(244,67,54, 0.2)",
      "&:hover": {
        backgroundColor: "rgb(244,67,54, 0.2)",
      },
    },
  },
}));

export { LearnerDetailsTable };
